import JSONApiService from "@/services/JSONApiService";
import APIService, { withPrefix } from "@/services/APIService";
import { OFFER_MODEL } from "@/models/offer-model";
import {
  OFFER_GROUP_TEMPLATE_MODEL,
  COPY
} from "@/models/offer-group-template-model";
import { OFFER_GROUP_MODEL } from "@/models/offer-group-model";
import { disablePluralizeForCall } from "@/services/utils";
import {
  include,
  nested,
  OFFER_GROUPS,
  OFFER_GROUP_DOCUMENTS,
  COMMENTS,
  APPLICATION,
  USER,
  BOOKING,
  AUTHOR,
  DOCUMENT
} from "@/models/relationships";
import pluralize from "pluralize";
import {
  OFFER_CONTRACT,
  SIGN,
  REQUEST_SIGN
} from "@/models/offer-contract-model";

const API = withPrefix(APIService.client(), "offers");

const WITHDRAW = "withdraw";
const COMPLETE = "complete";
const REQUEST_REFERENCES = "request-references";
const SUBMIT_FOR_APPROVAL = "submit-for-approval";
const APPROVE = "approve";
const REJECT = "reject";
const ACCEPT = "accept";
const MAIL_TO = "message-mailto";
const CONTRACTS = "contracts";

const offerIncludes = include(
  APPLICATION,
  OFFER_GROUPS,
  CONTRACTS,
  nested(OFFER_GROUPS, OFFER_GROUP_DOCUMENTS),
  nested(APPLICATION, USER),
  nested(APPLICATION, BOOKING)
);

const callOfferAction = (offerId, action, commit) => {
  const api = JSONApiService(commit);
  api.one(OFFER_MODEL, offerId);
  disablePluralizeForCall(api, () => api.all(action));
  return api.post({});
};

const callOfferDocumentAction = ({ offerId, documentId, action, commit }) => {
  const api = JSONApiService(commit);
  api.one(OFFER_MODEL, offerId);
  disablePluralizeForCall(api, () => {
    api.one(DOCUMENT, documentId);
    api.all(action);
  });
  return api.post({});
};

const callOfferStepAction = ({ stepId, action, commit }) => {
  const api = JSONApiService(commit);
  api.one(DOCUMENT, stepId);
  disablePluralizeForCall(api, () => api.all(action));
  return api.post({}, offerIncludes);
};

const offerApi = commit => ({
  getOffers: async params =>
    JSONApiService(commit)
      .all(OFFER_MODEL)
      .get({
        ...params,
        ...offerIncludes
      }),
  getOffer: async id =>
    JSONApiService(commit).find(OFFER_MODEL, id, offerIncludes),
  getOfferGroups: async id =>
    JSONApiService(commit)
      .one(OFFER_MODEL, id)
      .all(OFFER_GROUPS)
      .get(include(OFFER_GROUP_DOCUMENTS)),
  getOfferGroupTemplates: () =>
    JSONApiService(commit)
      .all(OFFER_GROUP_TEMPLATE_MODEL)
      .get(include(OFFER_GROUP_DOCUMENTS)),
  getOfferComments: async id =>
    JSONApiService(commit)
      .one(OFFER_MODEL, id)
      .all(COMMENTS)
      .get(include(AUTHOR)),
  getOfferDocumentComments: async (offerId, documentId) => {
    const api = JSONApiService(commit);
    api.one(OFFER_MODEL, offerId);
    disablePluralizeForCall(api, () => api.one(DOCUMENT, documentId));
    return api.all(COMMENTS).get(include(AUTHOR));
  },
  submitOfferForApproval: async offerId =>
    callOfferAction(offerId, SUBMIT_FOR_APPROVAL, commit),
  approveOffer: async offerId => callOfferAction(offerId, APPROVE, commit),
  markOfferStepComplete: async stepId =>
    callOfferStepAction({
      stepId,
      action: "complete",
      commit
    }),
  createOffer: async data =>
    JSONApiService(commit).create(OFFER_MODEL, data, offerIncludes),
  updateOffer: async data =>
    JSONApiService(commit)
      .one(OFFER_MODEL, data.id)
      .patch(data, offerIncludes),
  withdrawOffer: async offerId => callOfferAction(offerId, WITHDRAW, commit),
  completeOffer: async offerId => callOfferAction(offerId, COMPLETE, commit),
  rejectOffer: async offerId => callOfferAction(offerId, REJECT, commit),
  rejectDocument: async (offerId, documentId) =>
    callOfferDocumentAction({ offerId, documentId, action: REJECT, commit }),
  approveDocument: async (offerId, documentId) =>
    callOfferDocumentAction({ offerId, documentId, action: APPROVE, commit }),
  acceptOffer: async offerId => callOfferAction(offerId, ACCEPT, commit),
  requestOfferReferences: async offerId =>
    callOfferAction(offerId, REQUEST_REFERENCES, commit),
  getOfferMailto: async offerId => API.get(`${offerId}/${MAIL_TO}`),
  submitDocumentForApproval: async ({ documentId, offerId, data }) =>
    APIService.client().postFile(
      `${pluralize(OFFER_MODEL)}/${offerId}/document/${documentId}/response`,
      data
    ),
  getOfferGroupDocument: async ({ offerId, documentId }) => {
    const api = JSONApiService(commit);
    api.one(OFFER_MODEL, offerId);
    disablePluralizeForCall(api, () => api.one(DOCUMENT, documentId));
    return api.get();
  },
  addFileToOfferGroup: async ({ offerGroupId, offerId, data }) =>
    APIService.client().postFile(
      `${pluralize(
        OFFER_MODEL
      )}/${offerId}/${OFFER_GROUPS}/${offerGroupId}/documents`,
      data
    ),
  addContractToOffer: async ({ offerId, data }) =>
    APIService.client().postFile(
      `${pluralize(OFFER_MODEL)}/${offerId}/contracts`,
      data
    ),
  addOfferGroup: async ({ templateId, offerId }) => {
    const api = JSONApiService(commit);
    api.one(OFFER_MODEL, offerId).one(OFFER_GROUP_TEMPLATE_MODEL, templateId);
    disablePluralizeForCall(api, () => api.all(COPY));
    return api.post({});
  },
  requestSignOfferContract: async id => {
    const api = JSONApiService(commit);
    api.one(OFFER_CONTRACT, id);
    disablePluralizeForCall(api, () => api.all(REQUEST_SIGN));
    return api.post({});
  },
  signOfferContract: async id => {
    const api = JSONApiService(commit);
    api.one(OFFER_CONTRACT, id);
    disablePluralizeForCall(api, () => api.all(SIGN));
    return api.post({});
  },
  removeOfferGroup: async groupId =>
    JSONApiService(commit)
      .one(OFFER_GROUPS, groupId)
      .destroy(),
  editOfferGroup: async ({ groupId, data }) => {
    return JSONApiService(commit)
      .one(OFFER_GROUP_MODEL, groupId)
      .patch(data);
  },
  getContract: async id => JSONApiService(commit).find(OFFER_CONTRACT, id)
});
export default offerApi;
